// Ollaway travel-type quiz. Copy and scoring live in snippets/ollaway-quiz-app.liquid; this file only runs the flow.
(() => {
  const STORE_KEY = 'ollaway_quiz_v1';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  let uid = 0;

  const store = {
    get() { try { return JSON.parse(localStorage.getItem(STORE_KEY)); } catch { return null; } },
    set(value) { try { localStorage.setItem(STORE_KEY, JSON.stringify(value)); } catch { /* private mode */ } },
    clear() { try { localStorage.removeItem(STORE_KEY); } catch { /* private mode */ } }
  };

  const esc = value => String(value).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  // One Horizon Ring: a clear channel, part-filled with liquid whose surface stays level.
  function ring({ level = 0.5, amp = 0, dur = 3, delay = 0 } = {}) {
    const id = `olq-clip-${++uid}`;
    return `<svg class="olq-ring" viewBox="0 0 48 48" aria-hidden="true" focusable="false" style="--lvl:${((0.5 - level) * 30).toFixed(1)}px;--amp:${amp}deg;--dur:${dur}s;--delay:${delay}s">
      <defs><clipPath id="${id}"><circle cx="24" cy="24" r="15.5"/></clipPath></defs>
      <circle class="olq-ring__glass" cx="24" cy="24" r="15.5"/>
      <g clip-path="url(#${id})"><g class="olq-ring__sway"><rect class="olq-ring__liquid" x="-24" y="24" width="96" height="60"/></g></g>
      <circle class="olq-ring__frame" cx="24" cy="24" r="18.5"/>
    </svg>`;
  }

  const MARKS = {
    teaser: [{ level: .5, amp: 7, dur: 4.2 }, { level: .5, amp: 7, dur: 4.2, delay: -.4 }, { level: .5, amp: 7, dur: 4.2, delay: -.8 }, { level: .5, amp: 7, dur: 4.2, delay: -1.2 }],
    reader: [{ level: .38, amp: 3, dur: 5 }, { level: .38, amp: 3, dur: 5 }, { level: .62, amp: 3, dur: 5 }, { level: .62, amp: 3, dur: 5 }],
    winding: [{ level: .5, amp: 16, dur: 3.2 }, { level: .5, amp: 16, dur: 3.2, delay: -.25 }, { level: .5, amp: 16, dur: 3.2, delay: -.5 }, { level: .5, amp: 16, dur: 3.2, delay: -.75 }],
    sea: [{ level: .56, amp: 10, dur: 6 }, { level: .5, amp: 10, dur: 6, delay: -1.5 }, { level: .44, amp: 10, dur: 6, delay: -3 }, { level: .5, amp: 10, dur: 6, delay: -4.5 }],
    longhaul: [{ level: .5, amp: 4, dur: 1.6 }, { level: .5, amp: 4, dur: 1.9, delay: -.3 }, { level: .5, amp: 4, dur: 1.7, delay: -.6 }, { level: .5, amp: 4, dur: 2.1, delay: -.9 }],
    crew: [{ level: .62, amp: 12, dur: 3 }, { level: .42, amp: 6, dur: 4.4, delay: -1 }, { level: .52, amp: 9, dur: 3.6, delay: -2 }, { level: .34, amp: 14, dur: 2.8, delay: -.5 }],
    calc: [{ level: .5, amp: 22, dur: .9 }, { level: .5, amp: 22, dur: .9, delay: -.2 }, { level: .5, amp: 22, dur: .9, delay: -.4 }, { level: .5, amp: 22, dur: .9, delay: -.6 }]
  };
  const mark = (kind, extra = '') => `<div class="olq-mark olq-mark--${kind} ${extra}" aria-hidden="true">${(MARKS[kind] || MARKS.teaser).map(ring).join('')}</div>`;

  class Quiz {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('[data-olq-stage]');
      this.emailHost = root.querySelector('[data-olq-email]');
      try { this.config = JSON.parse(root.querySelector('[data-olq-config]').textContent); } catch { return; }
      this.code = root.dataset.code || '';
      this.percent = root.dataset.percent || '20';
      this.offerUrl = root.dataset.offerUrl || '/#ollaway-offer';
      this.dialog = root.closest('dialog');
      this.index = 0;
      this.answers = {};
      this.busy = false;
      this.stage.addEventListener('click', event => this.onClick(event));
      this.stage.addEventListener('keydown', event => this.onKey(event));
      this.emailHost?.querySelector('form')?.addEventListener('submit', event => this.onEmail(event));
      const saved = store.get();
      if (saved && this.config.types[saved.type]) { this.answers = saved.answers || {}; this.showResult(saved.type, false); }
      else this.showQuestion(0, 'forward', false);
      root.olq = this;
    }

    get total() { return this.config.questions.length; }

    swap(html, direction, focus = true) {
      const paint = () => {
        if (this.emailHost && this.emailHost.parentElement !== this.root) { this.emailHost.hidden = true; this.root.append(this.emailHost); }
        this.stage.innerHTML = html;
        this.stage.dataset.dir = direction;
        this.stage.classList.remove('is-leaving');
        this.stage.classList.add('is-entering');
        requestAnimationFrame(() => requestAnimationFrame(() => this.stage.classList.remove('is-entering')));
        this.afterPaint?.();
        this.afterPaint = null;
        if (focus) this.stage.querySelector('[data-olq-focus]')?.focus({ preventScroll: true });
        if (focus && !this.dialog) {
          const top = this.root.getBoundingClientRect().top;
          if (top < 0) scrollTo({ top: scrollY + top - 110, behavior: reduced.matches ? 'auto' : 'smooth' });
        } else if (this.dialog) this.dialog.querySelector('.olq-dialog__panel').scrollTop = 0;
        this.busy = false;
      };
      if (reduced.matches || !this.stage.children.length) return paint();
      this.busy = true;
      this.stage.dataset.dir = direction;
      this.stage.classList.add('is-leaving');
      setTimeout(paint, 170);
    }

    topbar(step) {
      const level = step / this.total;
      return `<div class="olq-top">
        <button type="button" class="olq-icon-btn" data-olq-back ${step === 0 ? 'disabled' : ''} aria-label="Previous question"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14.5 5.5 8 12l6.5 6.5"/></svg></button>
        <div class="olq-progress" role="status" aria-live="polite">
          <span class="olq-progress__ring" data-olq-progress>${ring({ level: Math.max(level, .08) })}</span>
          <span class="olq-progress__text">Question ${step + 1} <span>of ${this.total}</span></span>
        </div>
        ${this.dialog ? '<button type="button" class="olq-icon-btn" data-olq-close aria-label="Close quiz"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg></button>' : '<span class="olq-icon-btn olq-icon-btn--spacer" aria-hidden="true"></span>'}
      </div>`;
    }

    showQuestion(index, direction = 'forward', focus = true) {
      this.index = index;
      const question = this.config.questions[index];
      const chosen = this.answers[question.id];
      const cards = question.answers.map((answer, i) => `
        <button type="button" role="radio" class="olq-answer" data-olq-answer="${esc(answer.id)}" aria-checked="${chosen === answer.id}" tabindex="${chosen ? (chosen === answer.id ? 0 : -1) : (i === 0 ? 0 : -1)}" style="--i:${i}">
          <span class="olq-answer__radio" aria-hidden="true"></span>
          <span class="olq-answer__text"><strong>${esc(answer.label)}</strong>${answer.note ? `<small>${esc(answer.note)}</small>` : ''}</span>
          <kbd aria-hidden="true">${i + 1}</kbd>
        </button>`).join('');
      this.swap(`${this.topbar(index)}
        <div class="olq-q">
          <h3 class="olq-q__title" id="olq-q-${this.root.dataset.instance}" tabindex="-1" data-olq-focus>${esc(question.title)}</h3>
          ${question.hint ? `<p class="olq-q__hint">${esc(question.hint)}</p>` : ''}
          <div class="olq-answers${question.answers.length > 4 ? ' olq-answers--many' : ''}" role="radiogroup" aria-labelledby="olq-q-${this.root.dataset.instance}">${cards}</div>
        </div>`, direction, focus);
    }

    select(id) {
      if (this.busy) return;
      const question = this.config.questions[this.index];
      if (!question.answers.some(answer => answer.id === id)) return;
      this.busy = true;
      this.answers[question.id] = id;
      this.stage.querySelectorAll('[data-olq-answer]').forEach(button => button.setAttribute('aria-checked', String(button.dataset.olqAnswer === id)));
      // The liquid sloshes with the tap, rises a notch, then finds level again.
      const progress = this.stage.querySelector('[data-olq-progress] .olq-ring');
      if (progress && !reduced.matches) {
        progress.style.setProperty('--lvl', `${((0.5 - (this.index + 1) / this.total) * 30).toFixed(1)}px`);
        progress.classList.add('is-sloshing');
      }
      setTimeout(() => {
        this.busy = false;
        if (this.index + 1 < this.total) this.showQuestion(this.index + 1, 'forward');
        else this.calculate();
      }, reduced.matches ? 0 : 260);
    }

    calculate() {
      const type = this.score();
      const bundle = this.bundle();
      store.set({ type, bundle, answers: this.answers, at: Date.now() });
      document.dispatchEvent(new CustomEvent('ollaway:quiz:complete', { detail: { type, bundle } }));
      if (reduced.matches) return this.showResult(type);
      this.swap(`<div class="olq-calc" role="status" aria-live="polite">${mark('calc', 'is-leveling')}<p tabindex="-1" data-olq-focus>Finding your level…</p></div>`, 'forward');
      setTimeout(() => this.showResult(type), 1150);
    }

    score() {
      const totals = {};
      for (const question of this.config.questions) {
        const answer = question.answers.find(item => item.id === this.answers[question.id]);
        for (const [type, points] of Object.entries(answer?.scores || {})) totals[type] = (totals[type] || 0) + points;
      }
      const order = this.config.tiebreak;
      return order.slice().sort((a, b) => (totals[b] || 0) - (totals[a] || 0) || order.indexOf(a) - order.indexOf(b))[0];
    }

    bundle() {
      const { who, often } = this.answers;
      if (who === 'crew') return 'three';
      if (who === 'kid') return 'two';
      if (who === 'me' && (often === 'daily' || often === 'weekly')) return 'two';
      return 'one';
    }

    showResult(typeKey, focus = true) {
      const type = this.config.types[typeKey];
      const plan = this.config.plan;
      const bundle = this.config.bundles[this.bundle()];
      const tried = this.config.tried[this.answers.tried];
      // Shopify's /discount redirect keeps an encoded # as a literal path, so the anchor travels as a query param instead.
      const [path, hash] = this.offerUrl.split('#');
      const back = hash ? `${path || '/'}${path.includes('?') ? '&' : '?'}olq_go=${encodeURIComponent(hash)}` : this.offerUrl;
      const href = this.code ? `/discount/${encodeURIComponent(this.code)}?redirect=${encodeURIComponent(back)}` : this.offerUrl;
      const forKid = this.answers.who === 'kid';
      this.afterPaint = () => {
        const slot = this.stage.querySelector('[data-olq-email-slot]');
        if (slot && this.emailHost) {
          this.emailHost.querySelector('[data-olq-tags]').value = `newsletter,quiz,type-${typeKey}`;
          slot.append(this.emailHost);
          this.emailHost.hidden = false;
        }
      };
      this.swap(`<div class="olq-result">
        <div class="olq-result__top">
          <span class="olq-result__kicker">Your travel type</span>
          ${this.dialog ? '<button type="button" class="olq-icon-btn" data-olq-close aria-label="Close quiz"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg></button>' : ''}
        </div>
        <div class="olq-result__hero">
          ${mark(typeKey)}
          <div><h3 tabindex="-1" data-olq-focus>${esc(type.name)}</h3><p>${esc(type.line)}</p></div>
        </div>
        <div class="olq-result__grid">
          <section class="olq-card"><h4>Why it gets ${forKid ? 'them' : 'you'}</h4><p>${esc(type.why)}</p></section>
          <section class="olq-card olq-card--plan"><h4>Your Ollaway plan</h4>
            <ol class="olq-plan"><li>${esc(this.answers.speed === 'fast' ? plan.step1.fast : plan.step1.default)}</li><li>${esc(plan.step2)}</li><li>${esc(plan.step3)}</li></ol>
            <p class="olq-plan__tip">${esc(type.focus)}</p>
            ${tried ? `<p class="olq-plan__tip">${esc(tried)}</p>` : ''}
          </section>
          <section class="olq-card olq-card--bundle"><h4>What we'd pack</h4><p><strong>${esc(bundle.title)}.</strong> ${esc(bundle.text)}</p></section>
        </div>
        <div class="olq-reward">
          ${this.code ? `<div class="olq-reward__copy"><span class="olq-chip olq-chip--blue">Unlocked</span><h4>You unlocked ${esc(this.percent)}% off</h4>
            <p>Your code is applied when you use the button. Or copy it for later.</p></div>
            <div class="olq-reward__code"><code data-olq-code>${esc(this.code)}</code><button type="button" class="olq-copy" data-olq-copy aria-label="Copy code ${esc(this.code)}"><span data-olq-copy-label>Copy</span></button></div>` : ''}
          <a class="olq-btn olq-btn--blue" href="${esc(href)}" data-olq-cta>${this.code ? `Use my ${esc(this.percent)}% off` : 'Meet Ollaway'} <span aria-hidden="true">→</span></a>
        </div>
        <div data-olq-email-slot></div>
        <button type="button" class="olq-link" data-olq-retake>Retake the quiz</button>
      </div>`, 'forward', focus);
      syncTeasers();
    }

    onClick(event) {
      const answer = event.target.closest('[data-olq-answer]');
      if (answer) return this.select(answer.dataset.olqAnswer);
      if (event.target.closest('[data-olq-back]') && this.index > 0 && !this.busy) return this.showQuestion(this.index - 1, 'back');
      if (event.target.closest('[data-olq-close]')) return this.dialog?.close();
      if (event.target.closest('[data-olq-retake]')) { store.clear(); this.answers = {}; syncTeasers(); return this.showQuestion(0, 'back'); }
      const copy = event.target.closest('[data-olq-copy]');
      if (copy) {
        const label = copy.querySelector('[data-olq-copy-label]');
        const done = ok => { label.textContent = ok ? 'Copied' : 'Press ⌘C'; copy.classList.toggle('is-done', ok); setTimeout(() => { label.textContent = 'Copy'; copy.classList.remove('is-done'); }, 2000); };
        if (navigator.clipboard?.writeText) navigator.clipboard.writeText(this.code).then(() => done(true), () => done(false));
        else { const range = document.createRange(); range.selectNodeContents(this.stage.querySelector('[data-olq-code]')); const sel = getSelection(); sel.removeAllRanges(); sel.addRange(range); done(false); }
      }
    }

    onKey(event) {
      const group = event.target.closest('[role="radiogroup"]');
      const buttons = Array.from(this.stage.querySelectorAll('[data-olq-answer]'));
      if (!buttons.length || event.metaKey || event.ctrlKey || event.altKey) return;
      if (/^[1-9]$/.test(event.key) && !event.target.closest('input')) {
        const target = buttons[Number(event.key) - 1];
        if (target) { event.preventDefault(); target.focus(); this.select(target.dataset.olqAnswer); }
        return;
      }
      const move = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key];
      if (!move) return;
      event.preventDefault();
      const current = group ? buttons.indexOf(event.target.closest('[data-olq-answer]')) : -1;
      const next = buttons[(current + move + buttons.length) % buttons.length];
      buttons.forEach(button => { button.tabIndex = button === next ? 0 : -1; });
      next.focus();
    }

    async onEmail(event) {
      event.preventDefault();
      const form = event.currentTarget;
      const status = form.querySelector('[data-olq-email-status]');
      const input = form.querySelector('input[type="email"]');
      const button = form.querySelector('button[type="submit"]');
      const say = (text, state) => { status.textContent = text; form.dataset.state = state; };
      if (!input.value.trim() || !input.checkValidity()) { say('That email doesn’t look right. Mind checking it?', 'error'); input.setAttribute('aria-invalid', 'true'); return input.focus(); }
      input.removeAttribute('aria-invalid');
      button.disabled = true;
      say('Sending…', 'busy');
      try {
        const response = await fetch(form.action, { method: 'POST', body: new FormData(form), headers: { Accept: 'text/html' }, credentials: 'same-origin' });
        if (/challenge/.test(response.url)) throw new Error('challenge');
        if (!response.ok) throw new Error(String(response.status));
        say('Done. Your plan is on its way to your inbox.', 'success');
        form.querySelector('.olq-email-form__row').hidden = true;
      } catch (error) {
        button.disabled = false;
        say(error.message === 'challenge' ? 'Shopify wants to check you’re human. Tap “Email it to me” once more to finish in a new tab.' : 'That didn’t go through. Your result is still here, so try once more.', 'error');
        if (error.message === 'challenge') { form.target = '_blank'; form.addEventListener('submit', e => e.stopImmediatePropagation(), { capture: true, once: true }); }
      }
    }
  }

  // Teaser and announcement copy reflect a finished quiz for returning visitors.
  function syncTeasers() {
    const saved = store.get();
    document.querySelectorAll('[data-olq-teaser]').forEach(teaser => {
      const app = teaser.parentElement.querySelector('[data-olq]');
      const quiz = app?.olq;
      const type = saved && quiz?.config.types[saved.type];
      const chip = teaser.querySelector('[data-olq-teaser-chip]');
      const text = teaser.querySelector('[data-olq-teaser-text]');
      const cta = teaser.querySelector('[data-olq-teaser-cta]');
      for (const el of [chip, text, cta]) if (el && el.dataset.original === undefined) el.dataset.original = el.innerHTML;
      if (type) {
        if (chip) chip.textContent = quiz.code ? `Your ${quiz.percent}% is waiting` : 'Welcome back';
        if (text) text.textContent = `Your type: ${type.name}${quiz.code ? ` · your code: ${quiz.code}` : ''}`;
        if (cta) cta.innerHTML = 'See my plan <span aria-hidden="true">→</span>';
      } else for (const el of [chip, text, cta]) if (el) el.innerHTML = el.dataset.original;
    });
  }

  function openDialog(opener) {
    const dialog = document.querySelector('[data-olq-dialog]');
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    if (dialog.open) return true;
    dialog.showModal();
    document.documentElement.classList.add('olq-locked');
    dialog.querySelector('[data-olq-focus]')?.focus({ preventScroll: true });
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('olq-locked');
      if (location.hash === '#quiz-open') history.replaceState(null, '', location.pathname + location.search);
      opener?.focus?.({ preventScroll: true });
    }, { once: true });
    return true;
  }

  function init(root = document) {
    root.querySelectorAll('[data-olq-mark]').forEach(host => { if (!host.children.length) host.innerHTML = mark(host.dataset.olqMark); });
    root.querySelectorAll('[data-olq]').forEach(app => { if (!app.olq) new Quiz(app); });
    root.querySelectorAll('[data-olq-dialog]').forEach(dialog => {
      if (dialog.dataset.bound) return;
      dialog.dataset.bound = 'true';
      // A click on the backdrop lands on the dialog element itself.
      dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
    });
    syncTeasers();
  }

  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-ollaway-quiz-open], a[href$="#quiz-open"]');
    if (!opener) return;
    if (openDialog(opener)) return event.preventDefault();
    const inline = document.querySelector('.olq-page [data-olq]');
    if (inline) { event.preventDefault(); inline.scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth', block: 'center' }); }
  });

  // Arriving back from /discount/CODE: drop the helper param and land on the offer.
  function landOnOffer() {
    const url = new URL(location.href);
    const target = url.searchParams.get('olq_go');
    if (!target) return;
    url.searchParams.delete('olq_go');
    history.replaceState(null, '', `${url.pathname}${url.search}#${target}`);
    const el = document.getElementById(target);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: 'auto', block: 'start' }), 60);
  }

  const start = () => { init(); landOnOffer(); if (location.hash === '#quiz-open') openDialog(null); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  addEventListener('hashchange', () => { if (location.hash === '#quiz-open') openDialog(null); });
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
